import useOptimisticStatus, {
  useStatusCountUpdater,
} from 'Components/hooks/use-optimistic-status'
import useLoginCheckCallback from 'Components/hooks/use-login-check-callback'
import { useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import fetch from 'lib/decorated-fetch'
import serializeObject from '@arsenal/arsenal/modules/serializeObject'
import { DiggBuryContext } from 'Components/new/community-card/interact'
import { articleTypeDiggTypeMap } from 'Components/hooks/use-toggle-digg'
import { aid } from 'Constants/index'

export interface IToggleBuryParams {
  id?: string
  buried?: boolean
  buryCount?: number
  articleType?: number
}

// 反对
function useToggleBury({
  id,
  buried,
  buryCount,
  articleType,
}: IToggleBuryParams) {
  const { begin, rollback, curStatus, curCount } = useStatusCountUpdater({
    status: buried,
    count: buryCount,
  })

  const { actionsRef } = useContext(DiggBuryContext)

  useEffect(() => {
    if (actionsRef) {
      actionsRef.current.buryBegin = begin
    }
  }, [actionsRef, begin])

  useEffect(() => {
    if (actionsRef) {
      actionsRef.current.buryRollback = rollback
    }
  }, [actionsRef, rollback])

  useEffect(() => {
    if (actionsRef) {
      actionsRef.current.buryStatus = curStatus
    }
  }, [actionsRef, curStatus])

  const diggPrevRef =
    useRef<
      ReturnType<ReturnType<typeof useStatusCountUpdater>['begin']>['prev']
    >()

  return {
    update: useOptimisticStatus<[boolean, number]>({
      execute: useLoginCheckCallback(
        useCallback(
          ([curStatus]) => {
            return fetch(`/motor/community/digg_like`, {
              method: 'POST',
              headers: {
                'tt-anti-token':
                  window.localStorage.getItem('tt-anti-token') || '',
                'Content-Type': 'application/x-www-form-urlencoded',
              },
              body: serializeObject({
                aid,
                // user_id: userId,
                thread_id: id,
                group_id: id,
                digg_action: curStatus ? 'bury' : 'cancel_bury',
                like_type:
                  articleTypeDiggTypeMap[articleType!] || 'digg_thread',
              }),
            }).then((res) => res?.status === 0)
          },
          [id]
        ),
        useMemo(
          () => ({
            enter_method: 'bury',
          }),
          []
        )
      ),
      begin: useCallback(() => {
        if (actionsRef) {
          const { diggBegin, diggStatus } = actionsRef.current
          if (diggBegin && !curStatus && !diggStatus !== !curStatus) {
            diggPrevRef.current = diggBegin().prev
          }
        }
        return begin()
      }, [curStatus, begin]),
      rollback: useCallback(
        ([curStatus, curCount]) => {
          if (actionsRef) {
            const { diggRollback, diggStatus } = actionsRef.current
            if (diggRollback && !curStatus && !diggStatus !== !curStatus) {
              diggRollback(diggPrevRef.current)
            }
          }
          return rollback([curStatus, curCount])
        },
        [rollback]
      ),
    }),
    curStatus,
    curCount,
  }
}

export default useToggleBury
